// app/components/EditPageInlineClient.tsx
"use client";

import { useCallback, useEffect, useRef, useState } from "react";

type BreakpointValues = {
  mobile?: string;
  tablet?: string;
  desktop?: string;
};

type EditNode = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
  valuesByBreakpoint?: BreakpointValues;
};

type PendingImage = {
  file: File;
  key: string;
  originalUrl: string;
  isVideo?: boolean;
  isBackground?: boolean;
};

type ModalState = {
  open: boolean;
  title: string;
  message: string;
  variant: "success" | "error" | "info" | "confirm";
  onConfirm?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
};

type TextEditorState = {
  open: boolean;
  id: string;
  mobile: string;
  tablet: string;
  desktop: string;
  elementWidth?: number;
  elementHeight?: number;
  fontSizePx?: number; // เก็บ font-size ของ element จริง
};

type ImageEditorState = {
  open: boolean;
  id: string;
  src: string;
  // src ก่อนเลือกไฟล์ใหม่ — ใช้คืนค่า DOM element ถ้าปิด modal โดยไม่ Save
  originalSrc: string;
  fileName: string;
  width?: number;
  height?: number;
  sizeBytes?: number;
  downloadUrl?: string;
  isVideo?: boolean;
  isBackground?: boolean;
};

type MediaKind = "image" | "video" | "background";

declare global {
  interface Window {
    __CMS_EDIT_ALLOWED__?: boolean;
  }
}

/**
 * คืน URL ของไฟล์ต้นฉบับจริงจาก URL ที่แสดงอยู่บนหน้าเว็บ
 *
 * รูปเกือบทั้งเว็บถูกเสิร์ฟผ่าน next/image เป็น `/_next/image?url=<ของจริง>&w=..&q=..`
 * ถ้าตัด query ทิ้งตรง ๆ จะเหลือแค่ `/_next/image` ซึ่งทำให้ทุกอย่างพัง:
 * ชื่อไฟล์กลายเป็น "image", fetch เพื่ออ่านขนาด/มิติได้ 400, และที่ร้ายที่สุดคือ
 * OSS key กลายเป็น "_next/image" — กดเปลี่ยนรูปแล้วไฟล์จะถูกอัปโหลดไปคนละที่
 * ภาพบนจอเปลี่ยนเพราะสลับเป็น objectURL ชั่วคราว แต่พอ refresh รูปเดิมกลับมา
 */
function resolveOriginalMediaUrl(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    if (u.pathname === "/_next/image") {
      const inner = u.searchParams.get("url");
      if (inner) return new URL(inner, window.location.origin).href;
    }
    return u.href;
  } catch {
    return url;
  }
}

// ดึง key ของไฟล์จาก URL (ส่วนหลัง hostname)
function extractOssKeyFromUrl(url: string): string | null {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.replace(/^\/+/, "");
  } catch {
    return url.replace(/^https?:\/\/[^/]+\/?/, "").replace(/^\/+/, "");
  }
}

function collectEditableNodes(): HTMLElement[] {
  const selectors = [
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "li",
    "span",
    "img",
    "th",
    "td",
    "video",
  ].join(",");

  return Array.from(
    document.querySelectorAll<HTMLElement>(selectors)
  ).filter((el) => {
    const inHeader = el.closest("header");
    const inFooter = el.closest("footer");
    const inNav = el.closest("nav");
    const inButton = el.closest("button, a[role='button']");

    if (inHeader || inFooter || inNav || inButton) {
      return false;
    }

    const nearestCmsKey = el.closest("[data-cms-key]");
    if (nearestCmsKey && nearestCmsKey !== el) {
      return false;
    }

    return true;
  });
}

// resolve path + locale
function resolvePathAndLocale(): { path: string; locale: string } {
  const fullPath = window.location.pathname;
  const segments = fullPath.split("/").filter(Boolean);

  const KNOWN_LOCALES = ["th", "en", "zh", "cn"];

  let locale = (document.documentElement.lang || "en").toLowerCase();
  let path = fullPath;

  if (
    segments.length > 0 &&
    KNOWN_LOCALES.includes(segments[0].toLowerCase())
  ) {
    locale = segments[0].toLowerCase();
    const rest = segments.slice(1).join("/");
    path = rest ? `/${rest}` : "/";
  }

  if (!path) path = "/";

  if (
    locale === "zh-cn" ||
    locale === "zh_hans" ||
    locale === "zh-hans" ||
    locale === "cn"
  ) {
    locale = "zh";
  }

  return { path, locale };
}

// helper แปลง bytes → KB/MB/GB + format #,###
function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return "—";

  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;

  let value: number;
  let unit: "KB" | "MB" | "GB";

  if (bytes >= GB) {
    value = bytes / GB;
    unit = "GB";
  } else if (bytes >= MB) {
    value = bytes / MB;
    unit = "MB";
  } else {
    value = bytes / KB;
    unit = "KB";
  }

  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);

  return `${formatted} ${unit}`;
}

export default function EditPageInlineClient({
  initialAllowed,
}: {
  initialAllowed: boolean;
}) {
  const [enabled, setEnabled] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [modal, setModal] = useState<ModalState | null>(null);
  const [textEditor, setTextEditor] = useState<TextEditorState | null>(null);
  const [imageEditor, setImageEditor] = useState<ImageEditorState | null>(null);
  const [viewportSize, setViewportSize] = useState<{
    w: number;
    h: number;
  } | null>(null);

  // active tab: mobile / tablet / desktop
  const [activeTextTab, setActiveTextTab] = useState<
    "mobile" | "tablet" | "desktop"
  >("desktop");

  const pendingImagesRef = useRef<Map<string, PendingImage>>(new Map());

  const showModal = useCallback(
    (payload: Omit<ModalState, "open">) => {
      setModal({ ...payload, open: true });
    },
    []
  );

  const closeModal = useCallback(() => {
    setModal((m) => (m ? { ...m, open: false } : m));
  }, []);

  // auth + query
  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const wantsEdit =
      params.get("__editPage") === "1" ||
      params.get("__editmode") === "1";

    const allow = initialAllowed && wantsEdit;

    setEnabled(allow);
    window.__CMS_EDIT_ALLOWED__ = allow;
  }, [initialAllowed]);

  // assign stable IDs to editable nodes (runs for everyone, so public visitors
  // can match the selectors stored by the editor).
  useEffect(() => {
    if (typeof document === "undefined") return;

    const nodes = collectEditableNodes();
    nodes.forEach((el, index) => {
      el.dataset.cmsEditableId = el.dataset.cmsEditableId ?? String(index);
    });

    const bgNodes = Array.from(
      document.querySelectorAll<HTMLElement>("[data-cms-bg]")
    );
    bgNodes.forEach((el, index) => {
      if (!el.dataset.cmsEditableId) {
        el.dataset.cmsEditableId = `bg-${index}`;
      }
    });
  }, []);

  // viewport size
  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => {
      setViewportSize({
        w: window.innerWidth,
        h: window.innerHeight,
      });
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // hydrate text + image จาก API page-inline (runs for all visitors — public
  // visitors must also see the latest CMS content).
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    const { path, locale } = resolvePathAndLocale();

    (async () => {
      try {
        const params = new URLSearchParams({ path, locale });
        const res = await fetch(`/api/admin/page-inline?${params.toString()}`, {
          method: "GET",
          credentials: "include",
        });

        if (!res.ok) {
          console.warn("[inline hydrate] fetch failed", res.status);
          return;
        }

        const data = await res.json();
        if (!data?.ok || !data?.found || !Array.isArray(data.edits)) return;

        (data.edits as EditNode[]).forEach((edit) => {
          let el: HTMLElement | null = null;
          try {
            el = document.querySelector<HTMLElement>(edit.selector) || null;
          } catch {
            el = null;
          }

          if (!el && edit.cmsKey) {
            el = document.querySelector<HTMLElement>(
              `[data-cms-key="${edit.cmsKey}"]`
            );
          }

          if (!el) return;

          if (edit.type === "text") {
            if (edit.value) {
              el.innerText = edit.value;
            }

            const bp = edit.valuesByBreakpoint || {};
            if (bp.mobile) {
              el.setAttribute("data-cms-text-mobile", bp.mobile);
            }
            if (bp.tablet) {
              el.setAttribute("data-cms-text-tablet", bp.tablet);
            }
            if (bp.desktop) {
              el.setAttribute("data-cms-text-desktop", bp.desktop);
            }
          } else if (edit.type === "image") {
            const url = edit.value;
            if (!url) return;

            const tag = el.tagName.toLowerCase();
            const hasBg = el.hasAttribute("data-cms-bg");

            if (hasBg) {
              el.style.backgroundImage = `url("${url}")`;
            } else if (tag === "img") {
              (el as HTMLImageElement).src = url;
            } else if (tag === "video") {
              (el as HTMLVideoElement).src = url;
            }
          }
        });

        // trigger viewport effect อีกที ให้เลือก text ตามหน้าจอปัจจุบัน
        setViewportSize({
          w: window.innerWidth,
          h: window.innerHeight,
        });
      } catch (e) {
        console.error("[inline hydrate] error", e);
      }
    })();
  }, []);

  // apply text ต่อ viewport ทุกครั้งที่ viewport เปลี่ยน
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    const width = window.innerWidth;
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>(
        "[data-cms-text-mobile], [data-cms-text-tablet], [data-cms-text-desktop]"
      )
    );

    nodes.forEach((el) => {
      const mobile = el.getAttribute("data-cms-text-mobile") || "";
      const tablet = el.getAttribute("data-cms-text-tablet") || "";
      const desktop = el.getAttribute("data-cms-text-desktop") || "";

      let display = desktop || tablet || mobile || el.innerText;

      if (width < 768 && mobile) {
        display = mobile;
      } else if (width < 1024 && tablet) {
        display = tablet;
      } else if (desktop) {
        display = desktop;
      }

      if (el.innerText !== display) {
        el.innerText = display;
      }
    });
  }, [viewportSize?.w]);

  // เปิด modal media (image / video / background)
  const openMediaEditor = useCallback(
    (id: string, el: HTMLElement, kind: MediaKind) => {
      if (typeof window === "undefined") return;

      let rawSrc = "";
      let isVideo = false;
      let isBackground = false;

      if (kind === "background") {
        isBackground = true;
        const bg = window.getComputedStyle(el).backgroundImage;
        const match = /url\(["']?(.*?)["']?\)/i.exec(bg || "");
        if (!match || !match[1]) {
          showModal({
            title: "No background image",
            message:
              "This element does not have a detectable background-image URL.",
            variant: "info",
          });
          return;
        }
        rawSrc = match[1];
      } else if (kind === "video") {
        isVideo = true;
        const vid = el as HTMLVideoElement;
        rawSrc = vid.currentSrc || vid.src;
      } else {
        const img = el as HTMLImageElement;
        rawSrc = img.currentSrc || img.src;
      }

      const cleanSrc = resolveOriginalMediaUrl(rawSrc).split("?")[0];
      if (!cleanSrc) {
        showModal({
          title: "Cannot detect media path",
          message: "Cannot find media URL for this element.",
          variant: "error",
        });
        return;
      }

      let fileName = "(unknown)";
      try {
        const urlObj = new URL(cleanSrc, window.location.origin);
        fileName = urlObj.pathname.split("/").pop() || fileName;
      } catch {
        // ignore
      }

      const baseState: ImageEditorState = {
        open: true,
        id,
        src: cleanSrc,
        originalSrc: cleanSrc,
        fileName,
        isVideo,
        isBackground,
      };

      setImageEditor(baseState);

      // อ่าน metadata จาก URL ถ้า CORS อนุญาต
      (async () => {
        try {
          const res = await fetch(cleanSrc);
          if (!res.ok) return;
          const blob = await res.blob();
          const downloadUrl = URL.createObjectURL(blob);

          if (!isVideo && !isBackground) {
            const imgTmp = new Image();
            imgTmp.src = downloadUrl;
            imgTmp.onload = () => {
              setImageEditor((prev) =>
                prev && prev.id === id
                  ? {
                      ...prev,
                      sizeBytes: blob.size,
                      downloadUrl,
                      width: imgTmp.naturalWidth,
                      height: imgTmp.naturalHeight,
                    }
                  : prev
              );
            };
          } else {
            const v = document.createElement("video");
            v.src = downloadUrl;
            v.onloadedmetadata = () => {
              setImageEditor((prev) =>
                prev && prev.id === id
                  ? {
                      ...prev,
                      sizeBytes: blob.size,
                      downloadUrl,
                      width: v.videoWidth || prev.width,
                      height: v.videoHeight || prev.height,
                    }
                  : prev
              );
            };
          }
        } catch {
          // ignore
        }
      })();
    },
    [showModal]
  );

  // highlight + attach events (text + img + video)
  useEffect(() => {
    if (!enabled) return;
    if (typeof document === "undefined") return;

    const nodes = collectEditableNodes();

    nodes.forEach((el, index) => {
      el.dataset.cmsEditableId = el.dataset.cmsEditableId ?? String(index);
      el.classList.add("cms-edit-highlight");

      const tag = el.tagName.toLowerCase();
      const isImg = tag === "img";
      const isVideo = tag === "video";

      // IMAGE / VIDEO
      if (isImg || isVideo) {
        const mediaEl = el as HTMLImageElement | HTMLVideoElement;
        const id = el.dataset.cmsEditableId || "";
        const parent = mediaEl.parentElement as HTMLElement | null;
        if (!parent) return;

        const computed = window.getComputedStyle(parent);
        if (computed.position === "static") {
          parent.style.position = "relative";
        }

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "cms-edit-image-btn";
        btn.innerHTML = isVideo ? "🎬" : "🖼️";

        btn.style.position = "absolute";
        btn.style.top = "4px";
        btn.style.right = "4px";
        btn.style.padding = "4px 6px";
        btn.style.borderRadius = "999px";
        btn.style.border = "none";
        btn.style.cursor = "pointer";
        btn.style.fontSize = "12px";
        btn.style.lineHeight = "1";
        btn.style.background = "rgba(0,0,0,0.6)";
        btn.style.color = "#fff";
        btn.style.display = "flex";
        btn.style.alignItems = "center";
        btn.style.justifyContent = "center";
        btn.style.zIndex = "10";

        const onClickButton = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          openMediaEditor(id, el, isVideo ? "video" : "image");
        };

        const onClickMedia = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          openMediaEditor(id, el, isVideo ? "video" : "image");
        };

        btn.addEventListener("click", onClickButton);
        mediaEl.addEventListener("click", onClickMedia);

        (mediaEl as any)._cmsMediaBtn = btn;
        (mediaEl as any)._cmsOnClickMediaButton = onClickButton;
        (mediaEl as any)._cmsOnClickMedia = onClickMedia;

        parent.appendChild(btn);
        return;
      }

      // TEXT: dblclick -> modal 3 viewport + detect size + font-size
      const onDblClick = () => {
        const id = el.dataset.cmsEditableId || "";
        const baseText = el.innerText.trim();

        const mobileAttr = el.getAttribute("data-cms-text-mobile");
        const tabletAttr = el.getAttribute("data-cms-text-tablet");
        const desktopAttr = el.getAttribute("data-cms-text-desktop");

        const rect = el.getBoundingClientRect();
        const computed = window.getComputedStyle(el);
        const fontSizePx = parseFloat(computed.fontSize) || 13;

        setTextEditor({
          open: true,
          id,
          mobile: mobileAttr ?? baseText,
          tablet: tabletAttr ?? baseText,
          desktop: desktopAttr ?? baseText,
          elementWidth: rect.width || undefined,
          elementHeight: rect.height || undefined,
          fontSizePx,
        });

        // เปิดมาครั้งแรก ให้ default tab เป็น desktop
        setActiveTextTab("desktop");
      };

      el.addEventListener("dblclick", onDblClick);
      (el as any)._cmsOnDblClick = onDblClick;
    });

    return () => {
      nodes.forEach((el) => {
        el.classList.remove("cms-edit-highlight");
        el.removeAttribute("data-cms-editable-id");
        el.contentEditable = "false";
        el.removeAttribute("data-allow-context");

        const tag = el.tagName.toLowerCase();
        const isImg = tag === "img";
        const isVideo = tag === "video";

        if (isImg || isVideo) {
          const mediaEl = el as HTMLImageElement | HTMLVideoElement;
          const btn = (mediaEl as any)
            ._cmsMediaBtn as HTMLButtonElement | undefined;
          const btnHandler = (mediaEl as any)
            ._cmsOnClickMediaButton as ((e: Event) => void) | undefined;
          const mediaHandler = (mediaEl as any)
            ._cmsOnClickMedia as ((e: Event) => void) | undefined;

          if (btn && btnHandler) {
            btn.removeEventListener("click", btnHandler);
            btn.remove();
          }
          if (mediaHandler) {
            mediaEl.removeEventListener("click", mediaHandler);
          }
        }

        const dblHandler = (el as any)
          ._cmsOnDblClick as (() => void) | undefined;
        if (dblHandler) {
          el.removeEventListener("dblclick", dblHandler);
        }
      });
    };
  }, [enabled, openMediaEditor]);

  // background image elements: data-cms-bg
  useEffect(() => {
    if (!enabled) return;
    if (typeof document === "undefined") return;

    const bgNodes = Array.from(
      document.querySelectorAll<HTMLElement>("[data-cms-bg]")
    );

    bgNodes.forEach((el, index) => {
      if ((el as any)._cmsBgAttached) return;
      (el as any)._cmsBgAttached = true;

      if (!el.dataset.cmsEditableId) {
        el.dataset.cmsEditableId = `bg-${index}`;
      }
      el.classList.add("cms-edit-highlight");

      const computed = window.getComputedStyle(el);
      if (computed.position === "static") {
        el.style.position = "relative";
      }

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cms-edit-bg-btn";
      btn.innerHTML = "🖼️";

      btn.style.position = "absolute";
      btn.style.top = "6px";
      btn.style.right = "6px";
      btn.style.padding = "4px 6px";
      btn.style.borderRadius = "999px";
      btn.style.border = "none";
      btn.style.cursor = "pointer";
      btn.style.fontSize = "12px";
      btn.style.lineHeight = "1";
      btn.style.background = "rgba(0,0,0,0.6)";
      btn.style.color = "#fff";
      btn.style.zIndex = "10";

      const id = el.dataset.cmsEditableId || `bg-${index}`;

      const onClickBg = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        openMediaEditor(id, el, "background");
      };

      btn.addEventListener("click", onClickBg);

      (el as any)._cmsBgBtn = btn;
      (el as any)._cmsOnClickBg = onClickBg;

      el.appendChild(btn);
    });

    return () => {
      bgNodes.forEach((el) => {
        el.classList.remove("cms-edit-highlight");
        const btn = (el as any)
          ._cmsBgBtn as HTMLButtonElement | undefined;
        const handler = (el as any)
          ._cmsOnClickBg as ((e: Event) => void) | undefined;

        if (btn && handler) {
          btn.removeEventListener("click", handler);
          btn.remove();
        }
        (el as any)._cmsBgAttached = false;
      });
    };
  }, [enabled, openMediaEditor]);

  // apply text จาก modal (3 breakpoint)
  const applyTextEditor = useCallback(() => {
    if (!textEditor) return;

    const el = document.querySelector<HTMLElement>(
      `[data-cms-editable-id="${textEditor.id}"]`
    );
    if (!el) {
      setTextEditor(null);
      return;
    }

    const { mobile, tablet, desktop } = textEditor;
    const width =
      typeof window !== "undefined" ? window.innerWidth : 1200;

    let displayText = desktop;
    if (width < 768) {
      displayText = mobile;
    } else if (width < 1024) {
      displayText = tablet;
    }

    el.innerText = displayText;
    el.setAttribute("data-cms-text-mobile", mobile);
    el.setAttribute("data-cms-text-tablet", tablet);
    el.setAttribute("data-cms-text-desktop", desktop);

    setDirty(true);
    setTextEditor(null);
  }, [textEditor]);

  const handleSave = useCallback(async () => {
    if (!enabled) return;
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    const { path, locale } = resolvePathAndLocale();

    // upload pending media
    const pendingMap = pendingImagesRef.current;
    if (pendingMap.size > 0) {
      try {
        for (const [id, pending] of pendingMap.entries()) {
          const formData = new FormData();
          formData.append("file", pending.file);
          formData.append("key", pending.key);

          const res = await fetch("/api/admin/upload/oss/replace", {
            method: "POST",
            body: formData,
            credentials: "include",
          });

          if (!res.ok) {
            const text = await res.text().catch(() => "");
            console.error(
              "[inline image save] upload failed:",
              res.status,
              text
            );

            showModal({
              title: "Upload image failed",
              message: `Upload media failed (id=${id}): HTTP ${res.status}\n${text || ""}`,
              variant: "error",
            });

            return;
          }

          const el = document.querySelector<HTMLElement>(
            `[data-cms-editable-id="${id}"]`
          );
          if (!el) continue;

          const tag = el.tagName.toLowerCase();

          if (pending.isBackground || el.hasAttribute("data-cms-bg")) {
            el.style.backgroundImage =
              `url("${pending.originalUrl}?t=${Date.now()}")`;
          } else if (tag === "img") {
            const img = el as HTMLImageElement;
            img.src = pending.originalUrl + "?t=" + Date.now();
          } else if (tag === "video") {
            const vid = el as HTMLVideoElement;
            vid.src = pending.originalUrl + "?t=" + Date.now();
          }
        }

        pendingMap.clear();
      } catch (e) {
        console.error("[inline image save] upload error:", e);

        showModal({
          title: "Upload error",
          message: "Upload media error, please check console.",
          variant: "error",
        });

        return;
      }
    }

    // เก็บ edits
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>("[data-cms-editable-id]")
    );

    const edits: EditNode[] = nodes.map((el) => {
      const id = el.dataset.cmsEditableId || "";
      const tag = el.tagName.toLowerCase();
      const hasBg = el.hasAttribute("data-cms-bg");
      const isImg = tag === "img";
      const isVideo = tag === "video";

      const type: EditNode["type"] =
        isImg || isVideo || hasBg ? "image" : "text";

      let value = "";
      let valuesByBreakpoint: BreakpointValues | undefined;

      if (type === "image") {
        const pending = pendingImagesRef.current.get(id);
        if (pending) {
          value = pending.originalUrl;
        } else if (hasBg) {
          const bg = window
            .getComputedStyle(el)
            .backgroundImage.toString();
          const match = /url\(["']?(.*?)["']?\)/i.exec(bg || "");
          if (match && match[1]) {
            value = match[1].split("?")[0];
          } else {
            value = "";
          }
        } else if (isImg) {
          const img = el as HTMLImageElement;
          const rawSrc = img.currentSrc || img.src;
          value = rawSrc.split("?")[0];
        } else if (isVideo) {
          const vid = el as HTMLVideoElement;
          const rawSrc = vid.currentSrc || vid.src;
          value = rawSrc.split("?")[0];
        }
      } else {
        value = el.innerText.trim();

        const mobile =
          el.getAttribute("data-cms-text-mobile")?.trim() || "";
        const tablet =
          el.getAttribute("data-cms-text-tablet")?.trim() || "";
        const desktop =
          el.getAttribute("data-cms-text-desktop")?.trim() || "";

        const bp: BreakpointValues = {};
        if (mobile && mobile !== value) bp.mobile = mobile;
        if (tablet && tablet !== value) bp.tablet = tablet;
        if (desktop && desktop !== value) bp.desktop = desktop;

        if (bp.mobile || bp.tablet || bp.desktop) {
          valuesByBreakpoint = bp;
        }
      }

      const selector = `${tag}[data-cms-editable-id="${id}"]`;
      const cmsKey = el.getAttribute("data-cms-key") || undefined;

      return { id, type, selector, value, cmsKey, valuesByBreakpoint };
    });

    try {
      const res = await fetch("/api/admin/page-inline", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          path,
          locale,
          edits,
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        console.error("Save failed:", res.status, text);

        showModal({
          title: "Save failed",
          message: `Save failed: HTTP ${res.status}`,
          variant: "error",
        });

        return;
      }

      setDirty(false);

      showModal({
        title: "Saved",
        message: "Page changes have been saved successfully.",
        variant: "success",
      });
    } catch (err) {
      console.error(err);

      showModal({
        title: "Save error",
        message: "Unexpected error while saving. Please check console.",
        variant: "error",
      });
    }
  }, [enabled, showModal]);

  const handleDiscard = useCallback(() => {
    if (!enabled) return;
    if (!dirty) return;

    showModal({
      title: "Discard changes?",
      message:
        "All unsaved changes on this page will be lost. Do you want to continue?",
      variant: "confirm",
      confirmLabel: "Discard",
      cancelLabel: "Keep editing",
      onConfirm: () => {
        window.location.reload();
      },
    });
  }, [enabled, dirty, showModal]);

  // Ctrl/Cmd+S
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    const onKey = (e: KeyboardEvent) => {
      const isMac =
        typeof navigator !== "undefined" &&
        navigator.platform.toLowerCase().includes("mac");
      if (
        (isMac && e.metaKey && e.key === "s") ||
        (!isMac && e.ctrlKey && e.key === "s")
      ) {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener("keydown", onKey);
    return () =>
      window.removeEventListener("keydown", onKey);
  }, [enabled, handleSave]);

  if (!enabled) return null;

  const saveDisabled = !dirty;
  const discardDisabled = !dirty;

  // คำนวณความกว้าง modal ให้ใกล้เคียง element ที่แก้
  const textEditorDialogWidth =
    textEditor?.elementWidth != null
      ? Math.min(
          Math.max(Math.round(textEditor.elementWidth) + 40, 320),
          1200
        )
      : undefined;

  return (
    <>
      <div
        className="cms-edit-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <div
          style={{
            fontSize: "11px",
            color: "#6b7280",
            whiteSpace: "nowrap",
          }}
        >
          Viewport:{" "}
          {viewportSize
            ? `${viewportSize.w} × ${viewportSize.h}px`
            : "—"}
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            className="primary"
            onClick={handleSave}
            disabled={saveDisabled}
            style={{
              opacity: saveDisabled ? 0.5 : 1,
              cursor: saveDisabled ? "not-allowed" : "pointer",
            }}
          >
            💾 Save changes
          </button>
          <button
            type="button"
            onClick={handleDiscard}
            disabled={discardDisabled}
            style={{
              opacity: discardDisabled ? 0.5 : 1,
              cursor: discardDisabled ? "not-allowed" : "pointer",
            }}
          >
            ⟳ Discard
          </button>
        </div>
      </div>

      {/* Modal alert / confirm */}
      {modal && modal.open && (
        <div
          className="cms-modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="cms-modal"
            style={{
              maxWidth: "420px",
              width: "100%",
              background: "#fff",
              borderRadius: "12px",
              padding: "16px 20px",
              boxShadow:
                "0 10px 30px rgba(15,23,42,0.25)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "18px",
                }}
              >
                {modal.variant === "success" && "✅"}
                {modal.variant === "error" && "⚠️"}
                {modal.variant === "info" && "ℹ️"}
                {modal.variant === "confirm" && "❓"}
              </span>
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                {modal.title}
              </h3>
            </div>
            <p
              style={{
                margin: "0 0 16px 0",
                fontSize: "14px",
                whiteSpace: "pre-wrap",
              }}
            >
              {modal.message}
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              {modal.variant === "confirm" && modal.onConfirm ? (
                <>
                  <button
                    type="button"
                    onClick={closeModal}
                    style={{
                      borderRadius: "999px",
                      border: "none",
                      padding: "6px 14px",
                      fontSize: "14px",
                      cursor: "pointer",
                      background: "#e5e7eb",
                      color: "#111827",
                    }}
                  >
                    {modal.cancelLabel || "Cancel"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const fn = modal.onConfirm;
                      closeModal();
                      fn && fn();
                    }}
                    style={{
                      borderRadius: "999px",
                      border: "none",
                      padding: "6px 14px",
                      fontSize: "14px",
                      cursor: "pointer",
                      background: "#b91c1c",
                      color: "#fff",
                    }}
                  >
                    {modal.confirmLabel || "OK"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    borderRadius: "999px",
                    border: "none",
                    padding: "6px 14px",
                    fontSize: "14px",
                    cursor: "pointer",
                    background: "#111827",
                    color: "#fff",
                  }}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image / Video editor modal */}
      {imageEditor && imageEditor.open && (
        <div
          className="cms-modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            className="cms-modal cms-image-editor"
            style={{
              width: "90vw",
              maxWidth: "900px",
              background: "#fff",
              borderRadius: "12px",
              padding: "18px 20px",
              boxShadow:
                "0 10px 30px rgba(15,23,42,0.25)",
              display: "grid",
              gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1.4fr)",
              gap: "16px",
              maxHeight: "90vh",
              overflow: "hidden",
            }}
          >
            {/* preview */}
            <div
              style={{
                borderRadius: "8px",
                overflow: "hidden",
                background: "#f9fafb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "220px",
              }}
            >
              {imageEditor.isVideo ? (
                <video
                  src={imageEditor.src}
                  controls
                  style={{
                    maxWidth: "100%",
                    maxHeight: "320px",
                    display: "block",
                  }}
                />
              ) : (
                <img
                  src={imageEditor.src}
                  alt={imageEditor.fileName}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "320px",
                    display: "block",
                  }}
                />
              )}
            </div>

            {/* info + actions */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                fontSize: "13px",
                minHeight: 0,
                maxHeight: "100%",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "15px",
                  fontWeight: 600,
                }}
              >
                {imageEditor.isBackground
                  ? "Edit background media"
                  : imageEditor.isVideo
                  ? "Edit video"
                  : "Edit image"}
              </h3>

              <div
                style={{
                  background: "#f9fafb",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  display: "grid",
                  gap: "4px",
                }}
              >
                <div>
                  <strong>File name:</strong> {imageEditor.fileName}
                </div>
                <div>
                  <strong>Size:</strong>{" "}
                  {imageEditor.sizeBytes != null
                    ? formatFileSize(imageEditor.sizeBytes)
                    : "—"}
                </div>
                <div>
                  <strong>Dimensions:</strong>{" "}
                  {imageEditor.width && imageEditor.height
                    ? `${imageEditor.width} × ${imageEditor.height}px`
                    : "—"}
                </div>
                <div
                  style={{
                    wordBreak: "break-all",
                  }}
                >
                  <strong>URL:</strong> {imageEditor.src}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginTop: "6px",
                }}
              >
                {/* Download backup */}
                {(imageEditor.downloadUrl || imageEditor.src) && (
                  <a
                    href={imageEditor.downloadUrl || imageEditor.src}
                    download={imageEditor.fileName}
                    style={{
                      borderRadius: "999px",
                      padding: "6px 14px",
                      fontSize: "13px",
                      textDecoration: "none",
                      background: "#e5e7eb",
                      color: "#111827",
                    }}
                  >
                    ⬇ Download
                  </a>
                )}

                {/* Browse new */}
                <button
                  type="button"
                  onClick={() => {
                    if (!imageEditor) return;
                    if (typeof document === "undefined") return;

                    const { id, isVideo, isBackground, src } =
                      imageEditor;

                    const input =
                      document.createElement("input");
                    input.type = "file";
                    input.accept = isVideo ? "video/*" : "image/*";

                    input.onchange = () => {
                      const file = input.files?.[0];
                      input.remove();

                      if (!file) return;

                      const objectUrl =
                        URL.createObjectURL(file);
                      const cleanSrc =
                        resolveOriginalMediaUrl(src).split("?")[0];
                      const key = extractOssKeyFromUrl(cleanSrc);

                      if (!key) {
                        showModal({
                          title: "Cannot detect media path",
                          message:
                            "Cannot parse OSS key from media URL.",
                          variant: "error",
                        });
                        return;
                      }

                      let target: HTMLElement | null = null;

                      if (isBackground) {
                        target =
                          document.querySelector<HTMLElement>(
                            `[data-cms-bg][data-cms-editable-id="${id}"]`
                          ) ||
                          document.querySelector<HTMLElement>(
                            `[data-cms-bg="${id}"]`
                          );
                      } else {
                        target =
                          document.querySelector<HTMLElement>(
                            `[data-cms-editable-id="${id}"]`
                          );
                      }

                      if (target) {
                        const tag =
                          target.tagName.toLowerCase();
                        if (
                          isBackground ||
                          target.hasAttribute("data-cms-bg")
                        ) {
                          target.style.backgroundImage =
                            `url("${objectUrl}")`;
                        } else if (tag === "img") {
                          (target as HTMLImageElement).src =
                            objectUrl;
                        } else if (tag === "video") {
                          (target as HTMLVideoElement).src =
                            objectUrl;
                        }
                      }

                      const pendingMap =
                        pendingImagesRef.current;
                      pendingMap.set(id, {
                        file,
                        key,
                        originalUrl: cleanSrc,
                        isVideo,
                        isBackground,
                      });

                      setDirty(true);

                      setImageEditor((prev) =>
                        prev && prev.id === id
                          ? {
                              ...prev,
                              src: objectUrl,
                              sizeBytes: file.size,
                              downloadUrl: objectUrl,
                            }
                          : prev
                      );
                    };

                    document.body.appendChild(input);
                    input.click();
                  }}
                  style={{
                    borderRadius: "999px",
                    border: "none",
                    padding: "6px 14px",
                    fontSize: "13px",
                    cursor: "pointer",
                    background: "#111827",
                    color: "#fff",
                  }}
                >
                  🖼️ Browse new file
                </button>
              </div>

              <div
                style={{
                  marginTop: "auto",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    // ถ้า "Browse new file" แล้วปิด modal โดยไม่ Save: ต้องคืนค่า
                    // DOM element กลับไปเป็น src เดิมก่อน revoke blob URL — ไม่งั้น
                    // <img>/background จะค้างชี้ blob: ที่ถูก revoke แล้ว → โหลด
                    // ไม่ขึ้นเป็น net::ERR_FILE_NOT_FOUND จนกว่าจะ refresh หน้า
                    const pending = pendingImagesRef.current.get(
                      imageEditor.id
                    );
                    if (pending) {
                      const target = pending.isBackground
                        ? document.querySelector<HTMLElement>(
                            `[data-cms-bg][data-cms-editable-id="${imageEditor.id}"]`
                          ) ||
                          document.querySelector<HTMLElement>(
                            `[data-cms-bg="${imageEditor.id}"]`
                          )
                        : document.querySelector<HTMLElement>(
                            `[data-cms-editable-id="${imageEditor.id}"]`
                          );

                      if (target) {
                        if (
                          pending.isBackground ||
                          target.hasAttribute("data-cms-bg")
                        ) {
                          target.style.backgroundImage = `url("${imageEditor.originalSrc}")`;
                        } else {
                          const tag = target.tagName.toLowerCase();
                          if (tag === "img") {
                            (target as HTMLImageElement).src =
                              imageEditor.originalSrc;
                          } else if (tag === "video") {
                            (target as HTMLVideoElement).src =
                              imageEditor.originalSrc;
                          }
                        }
                      }

                      pendingImagesRef.current.delete(imageEditor.id);
                    }

                    if (imageEditor.downloadUrl) {
                      URL.revokeObjectURL(
                        imageEditor.downloadUrl
                      );
                    }
                    setImageEditor(null);
                  }}
                  style={{
                    borderRadius: "999px",
                    border: "none",
                    padding: "6px 14px",
                    fontSize: "14px",
                    cursor: "pointer",
                    background: "#e5e7eb",
                    color: "#111827",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Text editor modal: 3 size + Tab panel */}
      {textEditor && textEditor.open && (
        <div
          className="cms-modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="cms-modal cms-text-editor"
            style={{
              width: textEditorDialogWidth
                ? `${textEditorDialogWidth}px`
                : "90vw",
              maxWidth: "1200px",
              maxHeight: "90vh",
              background: "#fff",
              borderRadius: "12px",
              padding: "18px 20px",
              boxShadow:
                "0 10px 30px rgba(15,23,42,0.25)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: 600,
                marginBottom: "8px",
              }}
            >
              Edit text by viewport
            </h3>
            <p
              style={{
                margin: "0 0 10px 0",
                fontSize: "13px",
                color: "#6b7280",
              }}
            >
              Double-clicked element can have different text for
              mobile, tablet, and desktop. Current viewport will
              use the matching size.
            </p>

            {/* Tab header */}
            <div
              style={{
                display: "flex",
                gap: "4px",
                marginBottom: "10px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              {[
                { id: "mobile" as const, label: "Mobile (< 768px)" },
                { id: "tablet" as const, label: "Tablet (768–1023px)" },
                { id: "desktop" as const, label: "Desktop (≥ 1024px)" },
              ].map((tab) => {
                const active = activeTextTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTextTab(tab.id)}
                    style={{
                      border: "none",
                      padding: "6px 10px",
                      fontSize: "12px",
                      cursor: "pointer",
                      borderRadius: "999px 999px 0 0",
                      background: active ? "#111827" : "transparent",
                      color: active ? "#fff" : "#4b5563",
                      marginBottom: "-1px",
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Scrollable body */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                marginBottom: "16px",
              }}
            >
              {(() => {
                const bp = activeTextTab;
                const label =
                  bp === "mobile"
                    ? "Mobile (< 768px)"
                    : bp === "tablet"
                    ? "Tablet (768–1023px)"
                    : "Desktop (≥ 1024px)";

                const value =
                  bp === "mobile"
                    ? textEditor.mobile
                    : bp === "tablet"
                    ? textEditor.tablet
                    : textEditor.desktop;

                const onChange = (val: string) => {
                  setTextEditor((prev) =>
                    prev
                      ? {
                          ...prev,
                          [bp]: val,
                        }
                      : prev
                  );
                };

                const widthPx = textEditor.elementWidth
                  ? `${Math.round(textEditor.elementWidth)}px`
                  : "100%";

                const minHeightPx = textEditor.elementHeight
                  ? `${Math.max(
                      40,
                      Math.round(textEditor.elementHeight)
                    )}px`
                  : undefined;

                const fontSize = textEditor.fontSizePx || 13;

                return (
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: 500,
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    {label}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        overflowX: "auto",
                      }}
                    >
                      <textarea
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        rows={2}
                        style={{
                          width: widthPx,
                          maxWidth: "100%",
                          minHeight: minHeightPx,
                          fontSize: `${fontSize}px`,
                          padding: "6px 8px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          resize: "vertical",
                        }}
                      />
                    </div>
                  </label>
                );
              })()}
            </div>

            {/* Footer buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                paddingTop: "8px",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <button
                type="button"
                onClick={() => setTextEditor(null)}
                style={{
                  borderRadius: "999px",
                  border: "none",
                  padding: "6px 14px",
                  fontSize: "14px",
                  cursor: "pointer",
                  background: "#e5e7eb",
                  color: "#111827",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyTextEditor}
                style={{
                  borderRadius: "999px",
                  border: "none",
                  padding: "6px 14px",
                  fontSize: "14px",
                  cursor: "pointer",
                  background: "#111827",
                  color: "#fff",
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
